/* =====================================================================
   wt-app-delay  -  readable replacement for assets/global.min.js
   ---------------------------------------------------------------------
   Loaded synchronously in <head>, BEFORE {{ content_for_header }}, so it can
   act on the scripts Shopify injects there. It works together with:
     - the content_for_header string replace in layout/theme.liquid, which
       moves app scripts (asyncLoad) and a few Shopify scripts onto the
       'StartAsyncLoading' / 'StartPreviewBarLoading' events, and
     - assets/custom.min.js, which fires 'StartAsyncLoading' on the first
       scroll / mouse move / touch, or 5s after the load event.

   What global.min.js did (it was obfuscated): held back Shopify's trekkie
   analytics script and wrapped the web-pixels bootstrap (webPixelsManager:
   the Meta, Google, TikTok, Klaviyo... app pixels) in a 'StartAsyncLoading'
   listener, so visitors who left without touching the page within ~5s were
   never seen by any pixel. Its watcher also never stopped (it waited for a
   second web-pixels script that this store does not have) and re-scanned
   every script on the page on every DOM change.

   What this does (audit M3 + L9, 2026-10-15):
     - trekkie and the web-pixels bootstrap wait for 'StartAsyncLoading', as
       before (START_AT_LOAD below can start them right after load instead;
       measured as a speed cost, so it is off).
     - the RedRetarget (trackifyx) hook, if present, keeps the old rule:
       it waits for 'StartAsyncLoading'.
     - the watcher only looks at newly added scripts and stops when the
       HTML parser is done (DOMContentLoaded), since these scripts are all
       in the server-rendered <head>.
   ===================================================================== */
(function () {
  'use strict';
  if (window.__wtAppDelay) return;
  window.__wtAppDelay = 1;

  var EARLY = 'WtPixelsStart';
  var earlyFired = false;
  function fireEarly() {
    if (earlyFired) return;
    earlyFired = true;
    document.dispatchEvent(new CustomEvent(EARLY));
  }
  document.addEventListener('StartAsyncLoading', fireEarly);
  /* START_AT_LOAD: start trekkie + the app pixels on the first idle moment after load instead
     of waiting for 'StartAsyncLoading' (first touch/scroll, or 5s). Measured 2026-10-15
     (Lighthouse mobile, median of 3, preview theme): +170-250ms total blocking time and
     -5 to -14 performance points on home, fountain, bird and collection pages, in exchange
     for pixels seeing visitors who leave within ~5s without touching the page. OFF, which
     keeps the timing global.min.js had; flip to true only if the owner chooses attribution
     over that speed cost. */
  var START_AT_LOAD = false;
  if (START_AT_LOAD) {
    window.addEventListener('load', function () {
      var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 1); };
      idle(fireEarly, { timeout: 1500 });
    });
  }

  /* Run `code` (an inline script's text) once the named event has fired. */
  function wrap(code, eventName, flag) {
    return 'document.addEventListener(' + JSON.stringify(eventName) + ',function(){' +
             'if(window[' + JSON.stringify(flag) + '])return;window[' + JSON.stringify(flag) + ']=1;' +
             code + '\n});' +
           (eventName === EARLY ? 'if(window.__wtPixelsStarted)document.dispatchEvent(new CustomEvent(' + JSON.stringify(EARLY) + '));' : '');
  }
  document.addEventListener(EARLY, function () { window.__wtPixelsStarted = 1; });

  /* ---- Seal subscriptions: load early on the main product page ----
     Seal's purchase-options box sits inside the buy box, above Add to cart. Waiting for
     'StartAsyncLoading' (first scroll, or 5s after load) made it pop in 5-9s late, moving the
     button and changing the price under the shopper. When the page has a held Seal form
     (product-form[data-wt-seal-hold], snippets/product-info.liquid), Seal's own script from the
     asyncLoad list is started as soon as the HTML is parsed; the later asyncLoad copy is
     dropped by the insertBefore hook below. html.wt-seal-ready is set once Seal's stylesheet has
     loaded and its box has reached full height (custom-style.css shows the box then),
     html.wt-seal-giveup after 20s regardless. */
  var early = {}, root = document.documentElement;
  root.classList.add('wt-seal-pending');   /* custom-style.css: hold + hide only while this is set */
  function sealDone(cls) { root.classList.remove('wt-seal-pending'); if (cls) root.classList.add(cls); }
  function sealEarly() {
    if (!document.querySelector('product-form[data-wt-seal-hold]')) { sealDone(); return; }
    var url = null;
    for (var i = 0; i < document.scripts.length && !url; i++) {
      var t = document.scripts[i].text || '';
      if (t.indexOf('asyncLoad') === -1) continue;
      var m = t.match(/"(https:(?:\\\/|\/){2}cdn-app\.sealsubscriptions\.com(?:\\\/|\/)[^"]*sealsubscriptions\.js[^"]*)"/);
      if (m) { try { url = JSON.parse('"' + m[1] + '"'); } catch (e) {} }
    }
    if (url) {
      var s = document.createElement('script');
      s.async = true;
      s.src = url;
      early[s.src] = 1;
      document.head.appendChild(s);
    }
    var t0 = Date.now();
    function styled() {
      for (var j = 0; j < document.styleSheets.length; j++) {
        if (/sealsubscriptions\.com\//.test(document.styleSheets[j].href || '')) return true;
      }
      return false;
    }
    function filled() {   /* the box lays out while hidden (custom-style.css), so its height is real */
      var c = document.querySelectorAll('.product-info:has(product-form[data-wt-seal-hold]) .sealsubs-container');
      for (var j = 0; j < c.length; j++) if (c[j].offsetHeight >= 100) return true;
      return false;
    }
    (function poll() {
      if (styled() && filled()) { sealDone('wt-seal-ready'); return; }
      if (Date.now() - t0 > 20000) { sealDone('wt-seal-giveup'); return; }
      setTimeout(poll, 100);
    })();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sealEarly);
  else sealEarly();

  /* Rebuy add-on offer in the buy box: custom-style.css holds its space until Rebuy draws it. If Rebuy
     hasn't drawn after 10s (blocked, or nothing to offer), release the hold rather than keep a blank gap. */
  function rebuyGiveup() {
    if (!document.querySelector('[data-wt-main] [data-rebuy-id]')) return;
    setTimeout(function () {
      if (!document.querySelector('[data-wt-main] [data-rebuy-id] .rebuy-widget')) document.documentElement.classList.add('wt-rebuy-giveup');
    }, 10000);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', rebuyGiveup);
  else rebuyGiveup();

  /* ---- trekkie: hold its <script src> until EARLY ----
     Shopify's analytics snippet inserts trekkie with insertBefore. The first
     such insert is swapped for an inert placeholder and the real src is
     loaded later; after that, inserts pass straight through. */
  var held = [], holding = true;
  var nativeInsertBefore = Element.prototype.insertBefore;
  Element.prototype.insertBefore = function (node, ref) {
    var args = arguments;
    if (node && node.nodeName === 'SCRIPT' && node.src && early[node.src]) {
      /* already started by sealEarly(); a second copy would initialise Seal twice */
      args = [document.createComment('wt: ' + node.src.split('?')[0] + ' already loaded'), ref];
    } else if (holding && node && node.nodeName && node.nodeName.toLowerCase() === 'script' &&
        node.hasAttribute && node.hasAttribute('src') && /trekkie/i.test(node.src) && !earlyFired) {
      held.push(node.src);
      holding = false;
      var stub = document.createElement('script');
      stub.setAttribute('module', '');
      args = [stub, ref];
    }
    try {
      return nativeInsertBefore.apply(this, args);
    } catch (e) {
      /* the old loader's fallback: some snippets insert before a node that is not a child */
      return nativeInsertBefore.call(this, args[0], document.scripts[0]);
    }
  };
  document.addEventListener(EARLY, function () {
    holding = false;
    /* 750ms later, as global.min.js did, so trekkie starts behind the app scripts */
    setTimeout(function () { held.splice(0).forEach(function (src) {
      var s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = src;
      document.head.appendChild(s);
    }); }, 750);
  });

  /* ---- inline scripts in <head>: rewrite before the parser runs them ----
     A parser-inserted inline script runs after a microtask checkpoint, so a
     MutationObserver callback sees it (and its text) first. */
  var done = typeof WeakSet === 'function' ? new WeakSet() : null;
  function consider(node) {
    if (!node || node.nodeName !== 'SCRIPT' || node.src) return;
    if (done && done.has(node)) return;
    var text = node.text || '';
    /* The parser can pause mid-script on a network chunk boundary, so the text seen here
       may be partial. Wrapping partial text would leave the rest appended after the
       wrapper (a syntax error, no pixels). Only act on a complete script; the later
       text-node records for the same <script> bring us back here. A script whose ending
       changes shape is simply never wrapped and runs undelayed, which is harmless. */
    if (!/\}\)\(\);?\s*$/.test(text)) return;
    if (text.indexOf('MutationObserver') !== -1 || text.indexOf('StartAsyncLoading') !== -1 || text.indexOf(EARLY) !== -1) return;
    if (text.indexOf('webPixelsManager') !== -1) {
      if (done) done.add(node);
      node.text = wrap(text, EARLY, '__wtWpmRan');
    } else if (text.indexOf('trackifyx.redretarget.com/pull/whiskertons-ks.myshopify.com/hook.js') !== -1) {
      if (done) done.add(node);
      node.text = wrap(text, 'StartAsyncLoading', '__wtTrackifyRan');
    }
  }
  var observer = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var r = records[i];
      consider(r.type === 'characterData' ? r.target.parentNode : r.target);
      for (var j = 0; j < r.addedNodes.length; j++) {
        var n = r.addedNodes[j];
        consider(n.nodeName === 'SCRIPT' ? n : n.parentNode);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  document.addEventListener('DOMContentLoaded', function () { observer.disconnect(); });
})();
